import React, { ReactElement, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Navigate, useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import { SiteExp } from '@groma-dev/scaffold';
import { ToastMode, useDefineMessages, useToasts } from '@groma-dev/blueprint';
import { useIntl } from 'react-intl';
import { useAppStore, useDataStore } from '../../providers/StoresProvider';
import { AppConfig } from '../../../core/AppConfig';
import { AppRoutes } from '../../../core/routes';
import { FirebaseEvents } from '../../utils/Constants';
import { buildUrlWithParams } from '../../utils/WebHelper';
import { APIResponse } from '../../../core/apiclient/BaseAPIClient';
import { handleErrorLogging } from '../../../core/utils/CoreHelper';

const AuthRouter = observer(({ children }: { children: ReactElement }) => {
  const dataStore = useDataStore();
  const { firebase } = useAppStore();
  const { pathname, search } = useLocation();
  const { formatMessage } = useIntl();
  const { addToast } = useToasts();
  const messages = useDefineMessages();
  const { userState, appState } = dataStore;
  const { Settings, Pages } = AppConfig;
  const { Authentication, Server, Domains, Metrics } = Settings;
  const { enabled: HomeEnabled } = Pages.Home;
  const { enabled: requireAuthentication, cookie } = Authentication;
  const { name: authCookieName } = cookie;
  const { enabled: protectionEnabled, protectionKey, cookieName: appCookieName } = Server.passwordProtection;
  const authCookiePresent = Cookies.get(authCookieName);
  const appPasswordMatch = Cookies.get(appCookieName) === protectionKey;

  const [currentLocation, setCurrentLocation] = useState<string>('');
  const [initialized, setInitialized] = useState<boolean>(false);

  const firePageViewEvent = () => {
    if (Metrics.Firebase.enabled) {
      if (currentLocation !== pathname) {
        setCurrentLocation(pathname);
        firebase.fireEvent(FirebaseEvents.PAGE_VIEW, { path: pathname });
      }
    }
  };

  // App Password Protected
  if (protectionEnabled && !appPasswordMatch && pathname !== AppRoutes.Auth) {
    const returnUrl = encodeURIComponent(`${pathname}${search}`);

    return <Navigate to={`${buildUrlWithParams(AppRoutes.Auth)}?return_url=${returnUrl}`} replace />;
  } else if (protectionEnabled && appPasswordMatch && pathname === AppRoutes.Auth) {
    return <Navigate to={buildUrlWithParams(AppRoutes.root)} replace />;
    // App requires authentication
  }

  if ((protectionEnabled && appPasswordMatch) || !protectionEnabled) {
    if (requireAuthentication && !authCookiePresent) {
      const returnUrl = encodeURIComponent(`${pathname}${search}`);
      window.location.href = `${Domains.id.baseUrl}/signin?return_url=${returnUrl}&site_exp=${SiteExp.INVESTORS}`;
      return null;
    }

    if (!HomeEnabled && pathname === AppRoutes.root) {
      return <Navigate to={buildUrlWithParams(AppRoutes.Overview)} replace />;
    }
  }

  if (authCookiePresent && !userState.user && !userState.fetchingUser && !initialized) {
    dataStore
      .fetchUser()
      .then((res: APIResponse) => {
        if (res.error) {
          setTimeout(() => addToast(res.error?.message ?? formatMessage(messages.commonError), ToastMode.ERROR), 100);
        }
      })
      .catch(e => handleErrorLogging(e));
  }

  // handle redirect for Phone Verification
  if (userState.user && !userState.user.phone_number_verified) {
    const returnUrl = encodeURIComponent(`${pathname}${search}`);
    window.location.href = `${Domains.id.baseUrl}/verification/phone?site_exp=${SiteExp.INVESTORS}&return_url=${returnUrl}`;
    return null;
  }

  firePageViewEvent();
  if (!requireAuthentication && !authCookiePresent) dataStore.setAppInitialized(true);
  if (!initialized) setInitialized(true);

  // Once app is initialized, check to see what the appState is for the nonREITAccount boolean
  // IF it is a non REIT fund type, ensure the user is on Documents or Help and if not, redirect to Documents
  if (
    appState.initialized &&
    appState.nonREITAccount &&
    ![AppRoutes.Settings, AppRoutes.Documents, AppRoutes.Help].includes(pathname)
  ) {
    return <Navigate to={AppRoutes.Documents} replace />;
  }

  return children;
});

export default AuthRouter;
